import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Login.css';
import '../publicTheme.css';
import SmartStaysLogo from '../components/SmartStaysLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import API_URL from '../config';
import { getAuthToken, getAuthUser } from '../utils/authSession';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import { toEmailInput, emailInputProps } from '../utils/emailInput';

function Login() {
  const { t } = useTranslation();
  const location = useLocation();
  // Na het activeren komt de gebruiker hier met zijn e-mailadres al ingevuld.
  const [email, setEmail] = useState(() => toEmailInput(location.state?.email || ''));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'SmartStays - Inloggen';

    const token = getAuthToken();
    const userData = getAuthUser();

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        if (user.role === 'ADMIN') {
          window.location.href = '/admin';
        } else if (user.role === 'SCHOONMAKER') {
          window.location.href = '/cleaner';
        } else if (user.role === 'VERHUURDER') {
          window.location.href = '/dashboard';
        }
      } catch (e) {}
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target)) {
      setError(t('common.fillRequired'));
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/login`, {
        email: email.trim().toLowerCase(),
        password
      });

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        if (localStorage.getItem('isNativeApp') === null) {
          const urlParams = new URLSearchParams(window.location.search);
          const isNative = urlParams.get('native') === 'true';
          localStorage.setItem('isNativeApp', isNative ? 'true' : 'false');
        }

        if (response.data.user.role === 'ADMIN') {
          window.location.href = '/admin';
        } else if (response.data.user.role === 'SCHOONMAKER') {
          window.location.href = '/cleaner';
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const isNative = localStorage.getItem('isNativeApp') === 'true';

  return (
    <div className={`login-container${isNative ? ' login-container-native' : ''}`}>
      <div className="login-card">
        <div className="login-top-row" style={isNative ? { justifyContent: 'flex-end' } : undefined}>
          {!isNative && <Link to="/" className="login-back-link">&larr; {t('login.backToHome')}</Link>}
          <LanguageSwitcher />
        </div>
        <div className="login-header">
          <h1><SmartStaysLogo /></h1>
          <p>{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleLogin} noValidate onInput={clearFieldInvalid}>
          <div className="form-group">
            <label>{t('login.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(toEmailInput(e.target.value))}
              autoComplete="username"
              {...emailInputProps}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('login.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Link to="/forgot-password" className="forgot-password-link">{t('login.forgotPassword')}</Link>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading}>
            {loading ? t('login.loading') : t('login.submit')}
          </button>
        </form>

        {!isNative && (
          <div className="login-footer">
            <p className="no-account">
              {t('login.noAccount')} <Link to="/contact" className="contact-link">{t('login.requestQuote')}</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
